import { useState } from 'react';
import { type Ref } from 'react';

import { useIsMobileDevice } from '@hivebrite/react-hooks';

import { UjErrorBoundary } from 'hb-react/shared/components/ErrorBoundary';

import ActionBar from './components/ActionBar';
import CommentsList from './components/v3/CommentsList';
import { type CommentsListImperativeHandle } from './components/v3/CommentsList';
import { SORT_BY } from './constants';
import { CommentsContextProvider } from './contexts/CommentsContext';
import { useFocusedCommentSearchParams } from './hooks/useFocusedCommentSearchParams';
import { type Comment } from './types';

interface CommentsProps {
  commentableId: number;
  commentableType: string;
  commentsRef?: Ref<CommentsListImperativeHandle>;
  isOpenInModal?: boolean;
  onCommentsUpdate?: (comments: {
    comments: Array<Comment>;
    totalComments: number;
  }) => void;
  onNewCommentCallback?: () => void;
  options?: any;
  parentUrl?: string;
}

const Comments = ({
  commentsRef,
  commentableId,
  commentableType,
  isOpenInModal,
  onCommentsUpdate,
  onNewCommentCallback,
  options,
  parentUrl,
}: CommentsProps) => {
  const [sortBy, setSortBy] = useState(SORT_BY.RECENT);
  const isMobileDevice = useIsMobileDevice();

  const { commentId: focusedCommentId, replyId: focusedReplyId } =
    useFocusedCommentSearchParams();

  const handleChangeSortBy = (sortByParam) => {
    setSortBy(sortByParam);
  };

  return (
    <UjErrorBoundary
      context={{
        tags: {
          module: 'FO|CommentsV3',
        },
      }}
    >
      <CommentsContextProvider
        canDisplayEmojiPicker={!isMobileDevice}
        focusedCommentId={focusedCommentId}
        focusedReplyId={focusedReplyId}
        isOpenInModal={isOpenInModal}
        options={options}
        parentUrl={parentUrl}
        sortBy={sortBy}
      >
        {options?.votes?.enableVotes && (
          <ActionBar isLoading={false} onChangeSortBy={handleChangeSortBy} />
        )}
        <CommentsList
          commentsRef={commentsRef}
          commentableId={commentableId}
          commentableType={commentableType}
          onCommentsUpdate={onCommentsUpdate}
          onNewCommentCallback={onNewCommentCallback}
        />
      </CommentsContextProvider>
    </UjErrorBoundary>
  );
};

export default Comments;
